import { useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { veiculosApi } from '../../api/endpoints'
import { Badge } from '../../components/ui/Badge'
import { Button, IconButton } from '../../components/ui/Button'
import { Card } from '../../components/ui/Card'
import { EmptyState, ErrorState, LoadingState } from '../../components/ui/EmptyState'
import { Modal } from '../../components/ui/Modal'
import { Table, THead, Th, Td } from '../../components/ui/Table'
import { useToast } from '../../components/ui/Toast'
import { PageHeader } from '../../layout/PageHeader'
import { LocalRecarga, StatusVeiculo, TIPO_COMBUSTIVEL_LABEL, UNIDADE_ENERGIA_LABEL } from '../../lib/enums'
import { cn, formatDate, formatMoney } from '../../lib/utils'
import { BloqueioRecurso } from '../plano/BloqueioRecurso'
import { useRecurso } from '../plano/useRecurso'
import type { Abastecimento, RendimentoAbastecimento } from '../../types'
import { AbastecimentoFormModal } from './AbastecimentoFormModal'
import { ConsumoSecao } from './ConsumoSecao'
import { FiltroPeriodo } from './FiltroPeriodo'
import { formatKm, invalidarVeiculo, numeroBr, unidadeConsumo } from './veiculoUtils'

/** "Rendeu": km, consumo e custo por km do trecho que o abastecimento fecha, ou o motivo de não haver cálculo. */
export function RendeuTexto({ r, unidade }: { r: RendimentoAbastecimento; unidade: number }) {
  if (!r.calculado) return <span className="text-muted">{r.motivo || 'Sem cálculo'}</span>
  return (
    <span className="text-white">
      {r.km != null && <>{formatKm(r.km)} · </>}
      {r.kmPorUnidade != null && <span className="font-medium">{numeroBr(r.kmPorUnidade, 1)} {unidadeConsumo(unidade)}</span>}
      {r.custoPorKm != null && <> · {formatMoney(r.custoPorKm)}/km</>}
      {r.misto && <span className="text-muted"> · misto</span>}
    </span>
  )
}

function ancoras(a: Abastecimento) {
  if (a.tanqueCheio && a.estavaNaReserva) return 'Cheio + reserva'
  if (a.tanqueCheio) return 'Tanque cheio'
  if (a.estavaNaReserva) return 'Reserva'
  return 'Parcial'
}

/** Abastecimentos e consumo do veículo (duas abas da mesma página: /abastecimentos e /consumo). */
export function AbastecimentosPage({ aba }: { aba: 'lista' | 'consumo' }) {
  const { id = '' } = useParams()
  const navigate = useNavigate()
  const qc = useQueryClient()
  const toast = useToast()
  const modulo = useRecurso('veiculos.modulo')
  const recurso = useRecurso('veiculos.abastecimento')
  const [de, setDe] = useState('')
  const [ate, setAte] = useState('')
  const [formAberto, setFormAberto] = useState(false)
  const [editando, setEditando] = useState<Abastecimento | undefined>()
  const [excluindo, setExcluindo] = useState<Abastecimento | null>(null)

  const liberado = modulo.habilitado && recurso.habilitado
  const { data: v, isLoading: carregandoV, isError: erroV, error } = useQuery({
    queryKey: ['veiculos', id], queryFn: () => veiculosApi.obter(id), enabled: !!id && liberado,
  })
  const { data: itens = [], isLoading, isError } = useQuery({
    queryKey: ['veiculos', id, 'abastecimentos', de, ate],
    queryFn: () => veiculosApi.abastecimentos.list(id, { de: de || undefined, ate: ate || undefined }),
    enabled: !!id && liberado && aba === 'lista',
  })

  const remover = useMutation({
    mutationFn: (a: Abastecimento) => veiculosApi.abastecimentos.remover(id, a.id),
    onSuccess: () => { invalidarVeiculo(qc); toast.success('Abastecimento excluído.'); setExcluindo(null) },
    onError: (e: Error) => toast.error(e.message),
  })

  if (!liberado) {
    return <><PageHeader title="Abastecimentos" /><BloqueioRecurso mensagem={modulo.habilitado ? 'O registro de abastecimentos não está incluído no seu plano.' : 'O módulo de veículos não está incluído no seu plano.'} /></>
  }
  if (carregandoV) return <><PageHeader title="Abastecimentos" /><Card><LoadingState /></Card></>
  if (erroV || !v) return <><PageHeader title="Abastecimentos" /><Card><ErrorState message={(error as Error)?.message || 'Veículo não encontrado.'} /></Card></>

  const podeAgir = v.podeEditar && v.status === StatusVeiculo.Ativo
  const base = `/veiculos/${v.id}`
  const abrir = (a?: Abastecimento) => { setEditando(a); setFormAberto(true) }

  return (
    <>
      <PageHeader title="Abastecimentos" />

      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="text-lg font-semibold text-white">{v.apelido}</h3>
          <p className="text-13 text-muted">{v.modelo.marca} {v.modelo.modelo} · {formatKm(v.kmAtual)}</p>
        </div>
        {podeAgir && <Button icon="ri-add-line" onClick={() => abrir()}>Novo abastecimento</Button>}
      </div>

      <div className="mb-4 flex flex-wrap items-end justify-between gap-3">
        <div className="flex gap-1 rounded bg-surface p-1">
          {([['lista', 'Abastecimentos', `${base}/abastecimentos`], ['consumo', 'Consumo', `${base}/consumo`]] as const).map(([k, rotulo, to]) => (
            <button key={k} type="button" onClick={() => navigate(to)} className={cn('h-8 rounded px-4 text-13 font-medium', aba === k ? 'bg-primary/15 text-primary' : 'text-muted hover:text-white')}>{rotulo}</button>
          ))}
        </div>
        <FiltroPeriodo de={de} ate={ate} onChange={(d, a) => { setDe(d); setAte(a) }} />
      </div>

      {aba === 'consumo' ? <ConsumoSecao veiculoId={id} de={de} ate={ate} /> : (
        <Card>
          {isLoading ? <LoadingState /> : isError ? <ErrorState message="Não foi possível carregar os abastecimentos." /> : itens.length === 0 ? (
            <EmptyState icon="ri-gas-station-line" message={de || ate ? 'Nenhum abastecimento no período.' : 'Nenhum abastecimento registrado.'}
              action={podeAgir ? <Button variant="soft-primary" icon="ri-add-line" onClick={() => abrir()}>Registrar o primeiro</Button> : undefined} />
          ) : (
            <Table>
              <THead><tr><Th>Data</Th><Th>Km</Th><Th>Combustível</Th><Th className="text-right">Quantidade</Th><Th className="text-right">Valor</Th><Th>Como foi</Th><Th>Rendeu</Th><Th className="w-24" /></tr></THead>
              <tbody>
                {itens.map((a) => (
                  <tr key={a.id} className="border-t border-border/70 align-top">
                    <Td>{formatDate(a.data)}</Td>
                    <Td>{formatKm(a.odometro)}</Td>
                    <Td>
                      {TIPO_COMBUSTIVEL_LABEL[a.tipoCombustivel]}
                      <p className="text-2xs text-muted">{a.postoApelido || a.postoNome || (a.localRecarga === LocalRecarga.Casa ? 'Em casa' : '')}</p>
                    </Td>
                    <Td className="text-right">{numeroBr(a.quantidade, 2)} {UNIDADE_ENERGIA_LABEL[a.unidade]}</Td>
                    <Td className="text-right">
                      {formatMoney(a.localRecarga === LocalRecarga.Casa ? (a.custoEstimado ?? a.valorTotal) : a.valorTotal)}
                      <p className="text-2xs text-muted">{a.localRecarga === LocalRecarga.Casa ? 'estimado' : `${formatMoney(a.valorUnitario)}/${UNIDADE_ENERGIA_LABEL[a.unidade]}`}</p>
                    </Td>
                    <Td><Badge variant={a.tanqueCheio ? 'income' : a.estavaNaReserva ? 'warning' : 'muted'} className="normal-case">{ancoras(a)}</Badge></Td>
                    <Td><RendeuTexto r={a.rendimento} unidade={a.unidade} /></Td>
                    <Td>
                      {podeAgir && (
                        <div className="flex justify-end">
                          <IconButton icon="ri-edit-line" label="Editar" onClick={() => abrir(a)} />
                          <IconButton icon="ri-delete-bin-line" label="Excluir" tone="danger" onClick={() => setExcluindo(a)} />
                        </div>
                      )}
                    </Td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Card>
      )}

      <p className="mt-6 text-xs text-muted"><Link to={base} className="hover:text-primary"><i className="ri-arrow-left-line" aria-hidden /> Voltar para {v.apelido}</Link></p>

      <AbastecimentoFormModal key={editando?.id ?? 'novo'} open={formAberto} onClose={() => setFormAberto(false)} veiculo={v} abastecimento={editando} />

      <Modal open={!!excluindo} onClose={() => setExcluindo(null)} size="sm" title="Excluir abastecimento"
        footer={<><Button variant="light" onClick={() => setExcluindo(null)}>Cancelar</Button><Button variant="danger" icon="ri-delete-bin-line" loading={remover.isPending} onClick={() => excluindo && remover.mutate(excluindo)}>Excluir</Button></>}>
        {excluindo && (
          <p className="text-13 text-muted">
            Excluir o abastecimento de <span className="text-white">{formatDate(excluindo.data)}</span> ({formatMoney(excluindo.valorTotal)})?
            {excluindo.transacaoId ? ' O lançamento ligado também será excluído.' : ''} O consumo é recalculado.
          </p>
        )}
      </Modal>
    </>
  )
}
